import { UseTableInstanceProps } from 'react-table'
import { CSSObject, Thead, Tr, Th } from '@ritualco/jutsu'
import { MinData, RANK } from './utils'

const hiddenThead: CSSObject = {
  border: 'none',
  height: 0,
}

const hiddenTr: CSSObject = {
  boxShadow: 'unset',
  height: 0,
}

const hiddenTh: CSSObject = {
  border: 'none',
  boxShadow: 'unset',
  py: 0,
}

const getStyles = (showHeader: boolean): Record<string, CSSObject> => {
  if (showHeader) {
    return {
      thead: {},
      tr: {},
      th: {},
    }
  }
  return {
    thead: hiddenThead,
    tr: hiddenTr,
    th: hiddenTh,
  }
}

const getHeaderStyles = (id: string, dragHandleId?: string): CSSObject => {
  if (id === dragHandleId) {
    return { width: 6, p: 0 }
  }
  if (id === RANK) {
    return { width: 8, px: 2 }
  }
  return { px: 3 }
}

export type TableHeaderProps<D extends MinData> = {
  showHeader: boolean
  dragHandleId?: string
} & Pick<UseTableInstanceProps<D>, 'headerGroups'>

export const TableHeader = <D extends MinData>({
  showHeader,
  dragHandleId,
  headerGroups,
}: TableHeaderProps<D>) => {
  // We need to render the table header even if it's hidden to prevent
  // an issue where dragging the first row in the table causes all rows
  // below to display incorrectly. This is due to the fact that fixed
  // layout tables base columns widths on the first row, and dragging it
  // causes it to be removed from the normal document flow. Thus, the
  // first header row serves as the anchor for all column widths
  const { thead, tr, th } = getStyles(showHeader)
  const firstGroup = headerGroups[0]
  if (headerGroups.length === 1) {
    return (
      <Thead sx={thead}>
        <Tr {...firstGroup.getHeaderGroupProps()} sx={tr}>
          {firstGroup.headers.map(column => (
            <Th
              {...column.getHeaderProps()}
              sx={{
                ...th,
                ...getHeaderStyles(column.id, dragHandleId),
              }}
            >
              {showHeader && column.render('Header')}
            </Th>
          ))}
        </Tr>
      </Thead>
    )
  }
  const lastGroup = headerGroups[headerGroups.length - 1]
  return (
    <Thead sx={thead}>
      <Tr {...lastGroup.getHeaderGroupProps()} sx={hiddenTr}>
        {lastGroup.headers.map(column => (
          <Th
            {...column.getHeaderProps()}
            sx={{
              ...hiddenTh,
              ...getHeaderStyles(column.id, dragHandleId),
            }}
          />
        ))}
      </Tr>
      <Tr {...firstGroup.getHeaderGroupProps()} sx={tr}>
        {firstGroup.headers.map(column => (
          <Th
            {...column.getHeaderProps()}
            sx={{
              ...th,
              ...getHeaderStyles(column.id, dragHandleId),
            }}
          >
            {showHeader && column.render('Header')}
          </Th>
        ))}
      </Tr>
    </Thead>
  )
}
